import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { employeeService } from "../../services/api";
import { ModuleActionsFrame } from "./shared/ModuleActionsFrame";
import ModuleActionEmployeePanel from "./shared/ModuleActionEmployeePanel";
import { EMPLOYEE_ACTION_ICONS } from "./payroll/payrollActionChrome";

/**
 * Hub action key → RBAC screen under Employee → Actions.
 */
export const EMPLOYEE_ACTION_RBAC = {
  view_employee: { screen: "emp_act_view_employee", run: "edit" },
  generate_letter: { screen: "emp_act_generate_letter", run: "create" },
  confirmation: { screen: "emp_act_confirmation", run: "create" },
  transfer: { screen: "emp_act_transfer", run: "create" },
  resignation: { screen: "emp_act_resignation", run: "create" },
};

const ACTION_DEFS = [
  {
    key: "view_employee",
    label: "View Employee",
    blurb: "Open the employee profile for the selected employee.",
    openLabel: "View",
    handler: "onViewEmployee",
    listTitle: "Viewed list",
    emptyList: "No viewed / actioned employees recorded for this action yet.",
    processHint:
      "Search by emp code or name, then open the profile. Viewed list shows employees already opened from Employee Actions when history is available.",
  },
  {
    key: "generate_letter",
    label: "Generate Letter",
    blurb: "Generate an employment letter for the selected employee.",
    openLabel: "Generate",
    handler: "onGenerateLetter",
    listTitle: "Letter list",
    emptyList: "No letter generation records yet for this action.",
    processHint:
      "Select the employee who needs a letter. Letter list will show employees who already received letters from this action when history is stored.",
  },
  {
    key: "confirmation",
    label: "Confirmation",
    blurb: "Run confirmation workflow for the selected employee.",
    openLabel: "Confirm",
    handler: "onConfirmation",
    listTitle: "Confirmation list",
    emptyList: "No confirmation records yet.",
    processHint:
      "Select only employees still on probation who are due for confirmation. Confirmation list shows employees already in or past the confirmation workflow.",
  },
  {
    key: "transfer",
    label: "Transfer",
    blurb: "Start transfer workflow for the selected employee.",
    openLabel: "Transfer",
    handler: "onTransfer",
    listTitle: "Transfer list",
    emptyList: "No transfer requests yet.",
    processHint:
      "Select the employee to transfer, then complete the transfer form. Transfer list shows employees with transfer requests already raised.",
  },
  {
    key: "resignation",
    label: "Resignation",
    blurb: "Start resignation / separation for the selected employee.",
    openLabel: "Resign",
    handler: "onResignation",
    listTitle: "Separation list",
    emptyList: "No separation / resignation requests yet.",
    processHint:
      "Select the employee initiating resignation or separation. Separation list shows employees with separation requests already raised.",
  },
];

/**
 * Employee Actions — Process | actioned List.
 * Visibility / Process gated per Employee → Actions screen.
 *
 * @param {(screenKey: string, action?: string) => boolean} canActionScreen
 */
export default function EmployeeActionsHub({
  darkMode = false,
  employees = [],
  canActionScreen = () => false,
  onViewEmployee = null,
  onGenerateLetter = null,
  onConfirmation = null,
  onTransfer = null,
  onResignation = null,
}) {
  const handlers = {
    onViewEmployee,
    onGenerateLetter,
    onConfirmation,
    onTransfer,
    onResignation,
  };

  const visibleActions = useMemo(() => {
    return ACTION_DEFS.filter((a) => {
      const rbac = EMPLOYEE_ACTION_RBAC[a.key];
      if (!rbac || !canActionScreen(rbac.screen, "view")) return false;
      return typeof handlers[a.handler] === "function";
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canActionScreen,
    onViewEmployee,
    onGenerateLetter,
    onConfirmation,
    onTransfer,
    onResignation,
  ]);

  const [activeKey, setActiveKey] = useState(() => visibleActions[0]?.key || "");
  const [directory, setDirectory] = useState(() =>
    Array.isArray(employees) ? employees : []
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visibleActions.length) return;
    if (!visibleActions.some((a) => a.key === activeKey)) {
      setActiveKey(visibleActions[0].key);
    }
  }, [visibleActions, activeKey]);

  const loadDirectory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await employeeService.getEmployees({
        page: 1,
        limit: 400,
        excludeEmploymentTypes: "External,external",
      });
      let list = [];
      if (Array.isArray(response?.data)) list = response.data;
      else if (Array.isArray(response?.employees)) list = response.employees;
      setDirectory(list.length ? list : Array.isArray(employees) ? employees : []);
    } catch (err) {
      console.error("EmployeeActionsHub load:", err);
      setDirectory(Array.isArray(employees) ? employees : []);
    } finally {
      setLoading(false);
    }
  }, [employees]);

  useEffect(() => {
    loadDirectory();
  }, [loadDirectory]);

  const active = visibleActions.find((a) => a.key === activeKey) || visibleActions[0];
  const activeRbac = active ? EMPLOYEE_ACTION_RBAC[active.key] : null;
  const canRunActive =
    !!activeRbac && canActionScreen(activeRbac.screen, activeRbac.run || "view");

  if (!visibleActions.length) {
    return (
      <p className="mt-6 text-center text-sm text-[hsl(var(--ds-muted-fg))]">
        No employee actions available for your role.
      </p>
    );
  }

  return (
    <ModuleActionsFrame
      actions={visibleActions}
      activeKey={active?.key}
      onSelect={setActiveKey}
      icons={EMPLOYEE_ACTION_ICONS}
    >
      <ModuleActionEmployeePanel
        key={active?.key}
        darkMode={darkMode}
        actionKey={active?.key}
        title={active?.label}
        blurb={active?.blurb}
        processHint={active?.processHint || ""}
        listTitle={active?.listTitle || "Actioned list"}
        emptyList={active?.emptyList || "No actioned employees yet."}
        openLabel={active?.openLabel || "Continue"}
        Icon3d={EMPLOYEE_ACTION_ICONS[active?.key]}
        employees={directory}
        loadingEmployees={loading}
        listActionRecords={employeeService.listActionRecords}
        onRefreshEmployees={loadDirectory}
        onOpenEmployee={(emp) => {
          if (!canRunActive) {
            toast.error(
              `You can see ${active?.label || "this action"} but do not have permission to process it.`
            );
            return;
          }
          const fn = handlers[active?.handler];
          if (typeof fn === "function") fn(emp);
        }}
      />
    </ModuleActionsFrame>
  );
}
